import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useT } from "../lib/i18n";
import { ShoppingBag, User, LogOut, ShieldCheck, ChevronDown, Heart, Globe } from "lucide-react";

function LanguageSwitcher() {
    const { lang, setLang, langs } = useT();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);
    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="language-switcher"
                onClick={() => setOpen(o => !o)}
                className="border border-[var(--border)] px-3 py-2.5 rounded-full hover:bg-[var(--rose-tint)] transition flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest"
                aria-label="Language">
                <Globe size={14} strokeWidth={1.5} />
                {lang.toUpperCase()}
                <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-zinc-200 shadow-lg fade-in min-w-[140px] z-50">
                    {Object.values(langs).map(l => (
                        <button
                            key={l.code}
                            data-testid={`lang-option-${l.code}`}
                            onClick={() => { setLang(l.code); setOpen(false); }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center gap-2 ${lang === l.code ? "bg-zinc-50 font-bold" : ""}`}>
                            <span>{l.flag}</span>
                            <span>{l.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Navbar() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const { t } = useT();
    const loc = useLocation();
    const navigate = useNavigate();
    const [catalogOpen, setCatalogOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setCatalogOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    useEffect(() => { setCatalogOpen(false); }, [loc.pathname]);

    const link = (to, label, testId) => (
        <Link
            data-testid={testId}
            to={to}
            className={`text-sm font-medium tracking-normal transition-colors ${
                loc.pathname === to ? "text-[var(--rose-deep)]" : "text-[var(--text-2)] hover:text-[var(--rose-deep)]"
            }`}
        >
            {label}
        </Link>
    );

    const catalogActive = loc.pathname === "/shirts" || loc.pathname === "/hoodies" || loc.pathname === "/accessories";

    return (
        <header className="sticky top-0 z-40 bg-[var(--bg)]/90 backdrop-blur border-b border-[var(--border-soft)]" data-testid="main-navbar">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link to="/" data-testid="navbar-logo" className="flex flex-col leading-none">
                    <span className="font-display text-3xl" style={{ color: "var(--rose-deep)" }}>VdS Gift Shop</span>
                    <span className="font-script text-base -mt-1 ml-0.5" style={{ color: "var(--text-2)" }}>Stickers &amp; Tegeltjes</span>
                </Link>

                <nav className="hidden md:flex items-center gap-7">
                    {link("/stickers", "Stickers", "nav-stickers")}
                    {link("/tegeltjes", "Tegeltjes", "nav-tile-designer")}
                    {link("/etsen-op-glas", "Etsen op glas", "nav-etsen")}
                    {link("/accessoires", "Accessoires", "nav-accessoires")}
                    {link("/upload-logo", "Logo uploaden", "nav-logo-upload")}
                    {link("/maak-voor-mij", "Op maat", "nav-mvm")}
                    {link("/gift-card", "Cadeaubon", "nav-giftcard")}
                    {link("/faq", t("nav.faq"), "nav-faq")}
                    {link("/contact", t("nav.contact"), "nav-contact")}
                    {user?.role === "admin" && link("/admin", "Admin", "nav-admin")}
                </nav>

                <div className="flex items-center gap-2">
                    <LanguageSwitcher />
                    {user && (
                        <button
                            data-testid="navbar-wishlist-button"
                            onClick={() => navigate("/wishlist")}
                            className="border border-[var(--border)] p-2.5 rounded-full hover:bg-[var(--rose-tint)] transition"
                            aria-label="Favorieten"
                            title="Favorieten">
                            <Heart size={16} strokeWidth={1.5} />
                        </button>
                    )}
                    <button
                        data-testid="navbar-cart-button"
                        onClick={() => navigate("/cart")}
                        className="relative border border-[var(--border)] px-4 py-2.5 rounded-full hover:bg-[var(--rose-tint)] transition flex items-center gap-2"
                        aria-label="Cart"
                    >
                        <ShoppingBag size={16} strokeWidth={1.5} />
                        <span className="text-xs font-semibold" data-testid="cart-item-count">{count}</span>
                    </button>
                    {user ? (
                        <>
                            <Link
                                to="/my-orders"
                                data-testid="navbar-my-orders"
                                className="hidden md:inline-block btn-ghost"
                            >Mijn bestellingen</Link>
                            <span className="hidden md:flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-zinc-600 border border-zinc-200">
                                {user.role === "admin" ? <ShieldCheck size={14} /> : <User size={14} />}
                                {user.name}
                            </span>
                            <button
                                data-testid="navbar-logout-button"
                                onClick={logout}
                                className="btn-ghost flex items-center gap-1.5"
                                aria-label="Logout"
                            >
                                <LogOut size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" data-testid="navbar-login-link" className="btn-ghost">Login</Link>
                            <Link to="/register" data-testid="navbar-register-link" className="btn-primary">Sign Up</Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
